import React from 'react'
import { Theme } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import clsx from 'clsx'
import ListItemIcon, { listItemIconClasses } from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { SidebarMenuItemComponent } from './MenuItemComponent'
import { ISidebarMenuItem } from 'app/menu/components/Sidebar/Menu'
import { useTranslation } from 'react-i18next'
import SvgIcon from '@mui/material/SvgIcon'
import { SidebarMenuPopOver } from 'app/menu/components/Sidebar/MenuPopover'
import IconExpandLess from '@mui/icons-material/ExpandLess'
import IconExpandMore from '@mui/icons-material/ExpandMore'
import Collapse from '@mui/material/Collapse'
import List from '@mui/material/List'
import Box from '@mui/material/Box'
import CountryFeature, { useHasCountryFeatureFunc } from 'core/utils/FeatureFlags/CountryFeature'
import EnvironmentFeature, { useHasEnvironmentFeatureFunc } from 'core/utils/FeatureFlags/EnvironmentFeature'
import { RequiredPermission, usePermissionsChecker } from 'utils/Subscription/RequiredPermissions'
import { CheckResult } from '@cbgms/components/environment/environmentUtils'
import { useHasFeature } from '@cbgms/base/feature-flags'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    menuItem: {
      '&.active': {
        borderLeft: `5px solid ${theme.palette.primary.main}`,
        background: theme.palette.action.focus,
        [`& .${listItemIconClasses.root}`]: {
          color: theme.palette.common.white
        }
      },
      '&.activeParent': {
        borderLeft: `5px solid ${theme.palette.primary.main}`
      },
      '&:hover': {
        color: 'inherit'
      }
    },
    smallPadding: {
      padding: '8px',
      [`& .${listItemIconClasses.root}`]: {
        minWidth: 'unset',
        margin: 'auto'
      }
    },
    icon: {
      color: `${theme.palette.action.active} !important`,
      minWidth: '34px'
    },
    primary: {
      textOverflow: 'ellipsis',
      overflow: 'hidden'
    },
    nested: {
      paddingLeft: 0,
      borderLeft: `5px solid ${theme.palette.primary.main}`,
      '& a.active': {
        borderLeft: 0
      },
      '& a.activeParent': {
        borderLeft: 0
      }
    },
    nestedPadding: {
      paddingLeft: theme.spacing(2)
    },
    nestedCollapsed: {
      position: 'fixed',
      transform: 'translate3d(50px, -40px, 0)',
      backgroundColor: theme.palette.background.default,
      boxShadow: '2px 1px 3px rgba(0,0,0,0.1)'
    }
  })
)

interface SidebarMenuItemProps extends ISidebarMenuItem {
  drawerOpen: boolean
  className?: string
  ID: string
  popoverID: string | null
  anchorEl: HTMLElement | null
  handleClickAnchor: (event: React.MouseEvent<EventTarget>, ID: string) => void
  handleCloseAnchor: (url: string | undefined) => void
  isChild?: boolean
}

interface IRenderProps
  extends Pick<
    SidebarMenuItemProps,
    'translation' | 'name' | 'permission' | 'url' | 'materialIcon' | 'customIcon' | 'environments' | 'shopCountries' | 'feature' | 'exact'
  > {
  children: ISidebarMenuItem[]
}

// / getPropsToRender will return the props to render as a menu. replaces the submenu's with 1 item with a single root item.
const getPropsToRender = (props: SidebarMenuItemProps, visibleChildren: ISidebarMenuItem[]): IRenderProps => {
  // when exactly 1 visible child, render that child in place of parent item.
  if (visibleChildren?.length === 1) {
    return {
      ...visibleChildren[0],
      customIcon: props.customIcon, // inhered icon from parent
      materialIcon: props.materialIcon, // inhered icon from parent
      children: visibleChildren[0].children || [] // make sure this is not null
    }
  }

  return { ...props, children: props.children || [] }
}

export const SidebarMenuItem: React.FC<SidebarMenuItemProps> = props => {
  const { drawerOpen, className, ID, anchorEl, popoverID, handleClickAnchor, handleCloseAnchor } = props
  const { t } = useTranslation()
  const classes = useStyles()
  const permissionChecker = usePermissionsChecker()
  const countryChecker = useHasCountryFeatureFunc()
  const envChecker = useHasEnvironmentFeatureFunc()
  const hasFeature = useHasFeature()

  const visibleChildren = (props.children || []).filter(
    c =>
      (!c.environments || envChecker(c.environments)) &&
      (!c.shopCountries || countryChecker(c.shopCountries)) &&
      permissionChecker(c.permission)[0] &&
      (!c.feature || hasFeature(c.feature))
  )

  const { translation, name, permission, url, materialIcon, children, customIcon, environments, feature, shopCountries, exact } =
    React.useMemo(() => getPropsToRender(props, visibleChildren), [visibleChildren, props])

  const isExpandable = children && children.length > 0
  const [openParent, setOpenParent] = React.useState(false)
  const open = ((drawerOpen && !!anchorEl) || !drawerOpen) && popoverID === ID

  function handleClick(e: React.MouseEvent<HTMLElement>) {
    if (!isExpandable) {
      if (ID === popoverID) {
        // clicked on a child element, no need to close
        return false
      }
      // for non expanding items, we assume there is an valid url set we can navigate to.
      // allow click bubbling to trigger the the function
      setOpenParent(false)
      handleCloseAnchor(undefined)
      return false
    } else if (!openParent || popoverID !== ID) {
      // when the item has children and is expandable. clicking it will navigate to to first child and expand it.
      setOpenParent(true)
      handleClickAnchor(e, ID)
    } else {
      // clicking it when already expanded  will only close the menu item and not navigate
      setOpenParent(false)
      handleCloseAnchor(undefined)
      return e.preventDefault() // stop bubbling to avoid navigation
    }

    return false
  }

  const MenuItemRootDrawerClosed = (
    <SidebarMenuPopOver
      extendedClassName={className}
      name={name}
      permission={permission}
      translation={translation}
      url={isExpandable ? visibleChildren?.[0]?.url : url}
      materialIcon={materialIcon}
      customIcon={customIcon}
      drawerOpen={drawerOpen}
      items={children}
      ID={ID}
      popoverID={popoverID}
      anchorEl={anchorEl}
      handleClick={handleClickAnchor}
      handleClose={handleCloseAnchor}
      exact={exact}
    />
  )

  const MenuItemRootDrawerOpen = (
    <SidebarMenuItemComponent
      name={name}
      className={clsx(classes.menuItem, className)}
      onClick={handleClick}
      drawerOpen={drawerOpen}
      onHover={handleClickAnchor}
      ID={ID}
      exact={exact}
      items={children}
      link={isExpandable ? visibleChildren?.[0]?.url : url}
    >
      {!!materialIcon && (
        <ListItemIcon className={classes.icon}>
          <SvgIcon component={materialIcon} />
        </ListItemIcon>
      )}
      {!!customIcon && <Box className={classes.icon}>{customIcon}</Box>}
      {drawerOpen ? <ListItemText primary={t(translation)} classes={{ primary: classes.primary }} /> : null}
      {isExpandable && !open && drawerOpen && <IconExpandMore />}
      {isExpandable && open && drawerOpen && <IconExpandLess />}
    </SidebarMenuItemComponent>
  )

  const MenuItemChildren =
    isExpandable && drawerOpen ? (
      <Collapse timeout='auto' in={!!anchorEl && popoverID === ID} unmountOnExit>
        <List component='div' disablePadding className={drawerOpen ? classes.nested : classes.nestedCollapsed}>
          {children.map((item, index) => (
            <SidebarMenuItem
              {...item}
              key={index}
              drawerOpen={drawerOpen}
              className={classes.nestedPadding}
              ID={ID}
              anchorEl={anchorEl}
              popoverID={popoverID}
              isChild
              handleClickAnchor={handleClickAnchor}
              handleCloseAnchor={handleCloseAnchor}
            />
          ))}
        </List>
      </Collapse>
    ) : null

  return (
    <>
      {!feature || hasFeature(feature) ? (
        <EnvironmentFeature environments={environments || []} result={environments ? CheckResult.ShowOnMatch : CheckResult.HideOnMatch}>
          <CountryFeature countries={shopCountries}>
            <RequiredPermission permission={permission}>
              {drawerOpen ? MenuItemRootDrawerOpen : MenuItemRootDrawerClosed}
            </RequiredPermission>
          </CountryFeature>
        </EnvironmentFeature>
      ) : null}
      {MenuItemChildren}
    </>
  )
}
